import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import moment from 'moment'
import { useMutation, useQuery } from '@apollo/client'
import { Box, ButtonBase, Collapse, Grid, IconButton } from '@mui/material'
import {
  AutoAwesomeOutlined,
  RemoveDoneOutlined,
  DoneAllOutlined,
  AddLinkOutlined,
} from '@mui/icons-material'
import { colors, Snackbar, Tooltip } from '@resiliatech/birdsong'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import styled from '@emotion/styled'
import UPDATE_GOAL from 'Apollo/Mutations/UpdateGoal'
import GET_FUNDER_ORG_COHORT from 'Apollo/Queries/GetFunderOrgCohort'
import { GOALS } from 'Apollo/Queries/Goals'
import { useAppState } from 'Context/AppContext/AppContext'
import { Goal, GoalType, GoalUpdateInput } from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { analytics } from 'Modules/Analytics'
import getEnvironment from 'Modules/handleEnvironment'
import { categoriesIncludingOld, impactCategories } from 'TypeDefs/Goal'
import {
  AvatarInformation,
  AvatarTooltip,
  Categories,
  Category,
  CategoryAndStatus,
  ChipContainer,
  Date as DateLabel,
  DateInfo,
  Details,
  EditOptions,
  GoalCardContainer,
  GoalTitle,
  Owner,
  OwnerAndDates,
  RightStatus,
  TopSection,
} from './NewGoals.styled'
import { GoalModal } from '../GoalModal/GoalModal'
import { DeleteGoalModal } from '../GoalModal/DeleteGoalModal'
import { NonprofitOrgAvatars } from './NonprofitOrgsAvatars'
import { FUNDER_IMPACT_GOALS_PATH } from '../../GoalPage/utils'
import { CollaborativeGoalChip } from '../Chips/CollaborativeGoalChip'
import { checkCanGoalBeCompleted, getFunderCount } from './utils'
import { PercentageCard } from './PercentageCard'
import { GoalInfo } from './GoalInfo'
import { GoalTabs } from './GoalTabs'
import { CampaignGoalChip } from '../Chips/CampaignGoalChip'
import { GoalCompletionChip } from '../Chips/GoalCompletionChip'
import DropdownMenu from '../../../../../Utils/DropdownMenu'
import { GOALS_PATH } from '../types'

type GoalCardProps = {
  goal: Goal
  hasLinkedCampaign: boolean
  setPreSelectedGoal?: (goal: Goal) => void
  onOpenTab?: () => void
  goalCardId?: string // New prop for DOM ID
  // Props for external control of tabs and snapshot modal
  externalTabToOpen?: string | null
  externalSnapshotText?: string | null
  onExternalActionProcessed?: () => void
}

const GOAL_DATE_FORMAT = 'MM-DD-YYYY'
const IMPACT_GOALS_PATH = '/impact/goals'

const iconStyles = {
  color: colors.interactive.primaryEnabled,
  cursor: 'pointer ',
  fontSize: 'large',
  marginRight: '10px',
  verticalAlign: 'middle',
}

const iconColorStyles = { color: '#706666' }

export const NewGoalCard = (props: GoalCardProps) => {
  const {
    goal,
    hasLinkedCampaign,
    setPreSelectedGoal,
    onOpenTab,
    goalCardId,
    externalTabToOpen,
    externalSnapshotText,
    onExternalActionProcessed,
  } = props

  const { teamMap = {} } = useNonprofitOrgTeam()
  const {
    userConfig: { id, permissions },
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      role,
      platformPlan,
      activeFunders,
    },
  } = useAppState()
  const history = useHistory()

  // TODO: This can be deleted when product is done demoing to initial funders feature was made for
  const currentEnv = getEnvironment()
  const isLocalOrStaging = currentEnv === 'local' || currentEnv === 'staging'
  const canEditAndDeleteFunderGoalsForDemoPurposes =
    permissions?.includes('page:internal:self_serve') && isLocalOrStaging
  // END TODO

  const [showEditGoalModal, setShowEditGoalModal] = useState(false)
  const [showDeleteGoalModal, setShowDeleteGoalModal] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const isCampaignGoal = goal.type === GoalType.Campaign
  const campaignChipText = hasLinkedCampaign
    ? 'Connected to Resilia Campaigns'
    : 'Disconnected from Resilia Campaigns'

  const [goalTab, setGoalTab] = useState('')
  const [goalTabOpen, setGoalTabOpen] = useState(false)

  // Handle external tab opening and snapshot modal triggering
  useEffect(() => {
    if (externalTabToOpen && !goalTabOpen) {
      // Open the specified tab
      setGoalTab(externalTabToOpen)
      setGoalTabOpen(true)

      // If we have snapshot text and the tab is 'snapshots', we'll trigger the modal
      // This will be handled in the GoalTabs component

      // Only notify parent immediately if there's no snapshot text to process
      // If there is snapshot text, GoalTabs will handle the cleanup
      if (!externalSnapshotText || externalTabToOpen !== 'snapshots') {
        onExternalActionProcessed?.()
      }
    }
  }, [
    externalTabToOpen,
    externalSnapshotText,
    goalTabOpen,
    onExternalActionProcessed,
  ])

  const [updateGoal] = useMutation(UPDATE_GOAL, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
      {
        query: GOALS,
        variables: {
          activeOnly: true,
          includeOnboarding: true,
          nonprofitOrgId,
        },
      },
    ],
  })

  const { data: { funderOrgCohortQuery: { nonprofitOrgs = [] } = {} } = {} } =
    useQuery(GET_FUNDER_ORG_COHORT, {
      variables: {
        nonprofitOrgIds: [nonprofitOrgId],
        returnAllNonprofitsInCohort: true,
      },
      skip: activeFunders.length === 0,
    })

  const canMarkGoalIsComplete = checkCanGoalBeCompleted(goal)

  const displayMarkCompleteFunctions = canMarkGoalIsComplete

  const openTab = (tab: string) => {
    setGoalTabOpen((prevState) => {
      const wasTabOpen = prevState && tab === goalTab

      // Set the active tab or update for opening
      if (!wasTabOpen) {
        // If opening a tab, set it as active
        setGoalTab(tab)

        // Trigger scroll when opening a new tab
        onOpenTab?.()
      }

      // Return whether tab should be open
      return !wasTabOpen
    })
  }

  // Handle the tab closing animation completion
  const handleTransitionEnd = () => {
    // Only clear the tab state after the closing animation completes
    if (!goalTabOpen) {
      setGoalTab('')
    }
  }

  if (!goal) return null

  const goalCategory =
    goal.category && categoriesIncludingOld[goal.category]
      ? categoriesIncludingOld[goal.category]
      : ''
  const funderSharedGoal = goal.FunderOrgToSharedGoal.filter(
    (sharedGoal) => sharedGoal.goalId === goal.id && !sharedGoal.archivedAt
  )[0]
  const fundersSharedGoal = goal.FunderOrgToSharedGoal
  const funderCountForSharedGoal = getFunderCount(fundersSharedGoal)
  const sharedWithFunderIds = goal.FunderOrgToSharedGoal.map(
    (sharedGoal) => sharedGoal.funderOrgId
  )

  const isGoalSharedByFunder =
    funderSharedGoal && funderSharedGoal.isGoalSharedByFunder

  const isGoalSharedByNonprofit =
    funderSharedGoal && funderSharedGoal.isGoalSharedByNonprofit

  const dispatchGoalCardClicked = () => {
    analytics.trackActionCreator(goals.View_Goal_Details_Clicked)({
      goalId: goal.id,
      userId: id,
      goalType: goal.type,
      isGoalSharedWithFunder: !!funderSharedGoal,
    })
  }

  const dispatchEditGoalClicked = () => {
    analytics.trackActionCreator(initiatives.Edit_Goal_Clicked)({
      goalId: goal.id,
      goalType: goal.type,
      templateLibraryId: null,
    })
  }

  const handleRouteToGoal = () => {
    // If a tab is open, close it instead of navigating
    if (goalTabOpen) {
      setGoalTabOpen(false)
      setGoalTab('')
      return
    }

    const isFunderImpactPath = history.location.pathname.includes(
      FUNDER_IMPACT_GOALS_PATH
    )
    const funderImpactGoalPath = `${FUNDER_IMPACT_GOALS_PATH}/${goal.id}`

    if (isFunderImpactPath) {
      history.push(funderImpactGoalPath)
    }
    dispatchGoalCardClicked()
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowEditGoalModal(true)
    dispatchEditGoalClicked()
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteGoalModal(true)
  }

  const handleMagicClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (setPreSelectedGoal) {
      setPreSelectedGoal(goal)
    }

    analytics.trackActionCreator(initiatives.Create_Ai_Story_Clicked)({
      userId: id,
      goalId: goal.id,
      goalType: goal.type,
      templateLibraryId: goal.templateLibraryId,
    })
  }

  const handleMarkComplete = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleUpdateGoal({
      id: goal.id,
      isComplete: !goal.isComplete,
      shareWithFunderOrgIds: sharedWithFunderIds,
      ownerId: goal.owner?.id,
    })
  }

  const popToast = (message: string) => {
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  const handleUpdateGoal = async (goalUpdateInfo: GoalUpdateInput) => {
    const { data } = await updateGoal({
      variables: { goalUpdateInfo },
    })
    setShowEditGoalModal(false)
    if (data?.updateGoal.id) popToast('Your Goal has been updated')
    else {
      popToast('Failed to update Goal')
    }
  }

  const handleDeleteGoal = async (goalId: number) => {
    const archiveInput = { id: goalId, archivedAt: new Date() }
    const { data } = await updateGoal({
      variables: { goalUpdateInfo: archiveInput },
    })
    setShowDeleteGoalModal(false)
    analytics.trackActionCreator(goals.Delete_Goal_Submitted)({
      goalId: goal.id,
      userAccountType: role,
      platformPlan,
    })
    if (data?.updateGoal.id) {
      history.push(IMPACT_GOALS_PATH, { toastToDisplay: 'deleteGoal' })
    } else {
      popToast('Failed to delete Goal')
    }
  }

  // Always use ButtonBase since the card is always interactive
  const showAiButton = !!setPreSelectedGoal

  const categories = [
    ...(goal.impactCategory ? [impactCategories[goal.impactCategory]] : []),
    ...(goalCategory && goalCategory !== 'None' ? [goalCategory] : []),
  ]

  const ContentWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    height: 100%;
    font-family: 'Helvetica Neue';
  `

  const PercentageCardWrapper = styled(Box)`
    height: 100%;
    display: flex;
    flex-direction: column;
    font-family: 'Helvetica Neue';
  `

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent other click events from firing

    // Determine the path based on current location
    const isFunderImpactPath = history.location.pathname.includes(
      FUNDER_IMPACT_GOALS_PATH
    )
    const path = isFunderImpactPath
      ? `${FUNDER_IMPACT_GOALS_PATH}/${goal.id}`
      : `${GOALS_PATH}/${goal.id}?section=tasks`

    // Construct the full URL
    const cardUrl = `${window.location.origin}${path}`

    // Copy to clipboard using the Clipboard API
    navigator.clipboard.writeText(cardUrl).catch(() => {
      // Silently handle error; could implement a fallback method if needed
      popToast('Failed to copy link')
    })
  }

  const menuItems = [
    {
      id: 'edit',
      label: 'Edit',
      onClick: (e: React.MouseEvent) => handleEditClick(e),
      icon: <EditOutlinedIcon sx={iconStyles} />,
    },
    {
      id: 'delete',
      label: 'Delete',
      onClick: (e: React.MouseEvent) => handleDeleteClick(e),
      icon: <DeleteOutlinedIcon sx={iconStyles} />,
    },
    // Remove create story with AI for now until we clean up this functionality a bit
    // {
    //   id: 'store',
    //   label: 'Create story with AI',
    //   onClick: (e: React.MouseEvent) => handleMagicClick(e),
    //   icon: (
    //     <AutoAwesomeOutlined
    //       data-testid='aiStoryButton'
    //       sx={{ ...iconStyles, color: '#4D3D99' }}
    //     />
    //   ),
    //   hide: !showAiButton,
    // },
    {
      id: 'complete',
      label: goal.isComplete ? 'Mark as Incomplete' : 'Mark as Complete',
      onClick: (e: React.MouseEvent) => handleMarkComplete(e),
      hide: !displayMarkCompleteFunctions,
      icon: goal.isComplete ? (
        <RemoveDoneOutlined sx={iconStyles} />
      ) : (
        <DoneAllOutlined sx={iconStyles} />
      ),
    },
    {
      id: 'copyLink',
      label: 'Copy Link',
      onClick: (e: React.MouseEvent) => handleCopyLink(e),
      icon: <AddLinkOutlined sx={iconStyles} />,
    },
  ]

  return (
    <>
      <ButtonBase disableRipple>
        <GoalCardContainer
          id={goalCardId}
          onClick={handleRouteToGoal}
          isInteractive
        >
          <Details>
            <Grid container spacing={0} sx={{ width: '100%', height: '100%' }}>
              {/* Main Content - Column 1 */}
              <Grid
                item
                xs={9}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <ContentWrapper>
                  {/* Top Section with Categories, Title, Dates */}
                  <TopSection>
                    <CategoryAndStatus>
                      {categories.length > 0 && (
                        <Categories>
                          {categories.map((category) => (
                            <Category key={category}>{category}</Category>
                          ))}
                        </Categories>
                      )}
                      <RightStatus>
                        {isGoalSharedByFunder && (
                          <CollaborativeGoalChip
                            direction='from'
                            funderSharedGoalName={
                              funderSharedGoal?.funderOrg?.name || ''
                            }
                            iconColorStyles={iconColorStyles}
                          />
                        )}
                        {isGoalSharedByNonprofit && (
                          <CollaborativeGoalChip
                            direction='with'
                            funderSharedGoalName={
                              funderSharedGoal?.funderOrg?.name || ''
                            }
                            funderCount={funderCountForSharedGoal}
                            iconColorStyles={iconColorStyles}
                          />
                        )}
                        {!!nonprofitOrgs.length && isGoalSharedByFunder && (
                          <AvatarTooltip
                            title="You and other nonprofits are joining forces with your funder to achieve this important goal together. Collaborating is key to make community impact, we're thrilled to have you on board."
                            arrow
                            PopperProps={{
                              placement: 'left',
                            }}
                          >
                            <AvatarInformation>
                              <NonprofitOrgAvatars
                                funderOrgCohort={nonprofitOrgs}
                              />
                            </AvatarInformation>
                          </AvatarTooltip>
                        )}
                        {isCampaignGoal && (
                          <CampaignGoalChip
                            campaignChipText={campaignChipText}
                          />
                        )}
                        {goal.isComplete && (
                          <GoalCompletionChip
                            iconColorStyles={iconColorStyles}
                          />
                        )}
                      </RightStatus>
                      {showAiButton && (
                        <EditOptions>
                          <Tooltip
                            title='Create story with AI'
                            arrow
                            PopperProps={{
                              placement: 'left',
                            }}
                          >
                            <AutoAwesomeOutlined
                              onClick={handleMagicClick}
                              data-testid='aiStoryButton'
                              sx={{ ...iconStyles, color: '#4D3D99' }}
                            />
                          </Tooltip>
                        </EditOptions>
                      )}
                      <EditOptions>
                        <DropdownMenu menuItems={menuItems} />
                      </EditOptions>
                      {isGoalSharedByFunder &&
                        canEditAndDeleteFunderGoalsForDemoPurposes && (
                          <AvatarTooltip
                            title='These actions for funder-shared goals are only shown to certain internal users on local and staging environments.'
                            arrow
                            PopperProps={{
                              placement: 'left',
                            }}
                          >
                            <EditOptions>
                              <EditOutlinedIcon
                                onClick={handleEditClick}
                                sx={iconStyles}
                              />
                              <DeleteOutlinedIcon
                                onClick={handleDeleteClick}
                                sx={iconStyles}
                              />
                            </EditOptions>
                          </AvatarTooltip>
                        )}
                    </CategoryAndStatus>
                    {goal.title && (
                      <GoalTitle data-testid='goalTitle' variant='h5'>
                        {goal.title}
                      </GoalTitle>
                    )}
                    <OwnerAndDates>
                      {!funderSharedGoal && (
                        <Owner>
                          <PersonOutlineOutlinedIcon
                            sx={{ fontSize: '16px' }}
                          />
                          {goal?.owner && teamMap[goal.owner.id]
                            ? `${goal.owner.firstName} ${goal.owner.lastName}`
                            : 'Not Assigned'}
                        </Owner>
                      )}
                      <DateInfo>
                        <DateLabel variant='caption'>
                          Start Date:{' '}
                          {goal.startDate
                            ? moment
                                .utc(goal.startDate)
                                .format(GOAL_DATE_FORMAT)
                            : 'Not Specified'}
                        </DateLabel>
                        &bull;
                        <DateLabel variant='caption'>
                          Due Date:{' '}
                          {goal.endDate
                            ? moment.utc(goal.endDate).format(GOAL_DATE_FORMAT)
                            : 'Not Specified'}
                        </DateLabel>
                      </DateInfo>
                    </OwnerAndDates>
                  </TopSection>

                  {/* Tab Navigation */}
                  <ChipContainer>
                    <GoalInfo onSelect={openTab} currentTab={goalTab} />
                  </ChipContainer>
                </ContentWrapper>
              </Grid>

              {/* Percentage Card - Column 2 */}
              <Grid
                item
                xs={3}
                style={{
                  borderBottom: `1px solid ${colors.interactive.borderEnabledHoverGray}`,
                }}
              >
                <PercentageCardWrapper>
                  <PercentageCard goal={goal} />
                </PercentageCardWrapper>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {/* Tabs Collapsible Content */}
                <Box
                  sx={{ display: 'flex' }}
                  style={{
                    width: '100%',
                    background: '#E8F3FF',
                  }}
                >
                  <Collapse
                    in={goalTabOpen}
                    collapsedSize={0}
                    style={{ width: '100%' }}
                    onTransitionEnd={handleTransitionEnd}
                    timeout='auto'
                  >
                    <div>
                      {/* Show GoalTabs when goalTab is set, rather than using goalTabOpen */}
                      {goalTab && (
                        <GoalTabs
                          goal={goal}
                          tab={goalTab}
                          isGoalSharedWithFunder={!!funderSharedGoal}
                          externalSnapshotText={externalSnapshotText}
                          onExternalActionProcessed={onExternalActionProcessed}
                        />
                      )}
                    </div>
                  </Collapse>
                </Box>
              </Grid>
            </Grid>
          </Details>
        </GoalCardContainer>
      </ButtonBase>

      {showEditGoalModal && (
        <GoalModal
          isOpen={showEditGoalModal}
          onClose={() => setShowEditGoalModal(false)}
          goal={goal}
          onUpdateGoal={handleUpdateGoal}
        />
      )}
      {showDeleteGoalModal && (
        <DeleteGoalModal
          isOpen={showDeleteGoalModal}
          onClose={() => setShowDeleteGoalModal(false)}
          onDeleteGoal={handleDeleteGoal}
          goalId={goal.id}
          isFunderSharedGoal={!!funderSharedGoal}
        />
      )}
      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={snackbarMessage}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        action={
          <IconButton
            aria-label='close'
            color='inherit'
            sx={{ p: 0.5 }}
            onClick={() => setShowSnackbar(false)}
          >
            <CloseIcon />
          </IconButton>
        }
      />
    </>
  )
}
